import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { Component, EventEmitter, Input, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { KeyValue } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { ActivityActionModalComponent } from './activity-action-modal/activity-action-modal.component';
import { Subject } from 'rxjs';
import { Constant } from 'src/app/constant/constant';
import { environment } from 'src/environments/environment';

import { LeadActivityService } from 'src/app/services/lead-activity.service';
import { TemplateRef, ViewChild } from '@angular/core';

@Component({
  selector: 'calsoft-activity-new',
  templateUrl: './activity-new.component.html',
  styleUrls: ['./activity-new.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('open', style({ height: '*', opacity: 1, padding: '8px 0' })),
      state('closed', style({ height: '0px', opacity: 0, padding: '0' })),
      transition('open <=> closed', animate('200ms ease-in-out')),
    ]),
  ]
})
export class ActivityNewComponent implements OnDestroy {

  @ViewChild('contentDialog') contentDialog!: TemplateRef<any>;

  url = '';
  sanitizedUrl = '';
  generatingAiMap: { [key: number]: boolean } = {};
  selectedActivity: any = null;

  constructor(private dialog: MatDialog, private router: Router, private cdr: ChangeDetectorRef, private leadActivityService: LeadActivityService) {
    this.url = this.router.url || '';
    this.sanitizedUrl = this.sanitizeUrl(this.url);
    this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy)).subscribe(() => {
      this.url = this.router.url || '';
      this.sanitizedUrl = this.sanitizeUrl(this.url);
    });

    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
      .subscribe(() => { this.url = this.router.url || ''; });
  }

  generateAiAnalysis(activity: any): void {
    if (!activity || !activity.id) return;
    this.generatingAiMap[activity.id] = true;
    this.leadActivityService.generateAiForActivity(activity.id).subscribe(
      (res: any) => {
        this.generatingAiMap[activity.id] = false;
        const updated = res.data || res;
        if (updated) {
          activity.transcript = updated.transcript;
          activity.callSummary = updated.callSummary;
          activity.report = updated.report;
        }
        this.cdr.detectChanges();
      },
      (err: any) => {
        this.generatingAiMap[activity.id] = false;
        this.cdr.detectChanges();
      }
    );
  }

  viewReport(activity: any): void {
    this.selectedActivity = activity;
    this.dialog.open(this.contentDialog, { width: '700px' });
  }

  closeModal(): void {
    this.dialog.closeAll();
  }

  @Input() groupedActivities: any;


  @Input() type: any;

  @Input() spinner: boolean = false;

  @Input() onScrollValue: boolean = true;

  @Input() useExternalData = false;

  @Input() page = 0;

  @Input() leadActivityList: any = [];

  @Input() pageSize: any;

  @Input() leadActivityListFinal: any;

  @Input() totalSize;

  @Input() leadid: any;

  // Comparator for sorting dates in descending order (latest date first)
  dateDescOrder = (a: KeyValue<string, any>, b: KeyValue<string, any>): number => {
    const dateA = new Date(a.key);
    const dateB = new Date(b.key);
    return dateB.getTime() - dateA.getTime();
  };

  // Returns the appropriate icon name based on activity type
  getIconForActivity(type: string): string {
    if (!type) return 'history_toggle_off';
    const t = type.toLowerCase();
    if (t.includes('email')) return 'mail_outline';
    if (t.includes('payment')) return 'payments';
    switch (t) {
      case 'task': return 'check_box_outline_blank';
      case 'meeting': return 'groups';
      case 'stagechange': return 'track_changes';
      case 'ownerchange': return 'person_outline';
      case 'dealstatuschange': return 'track_changes';
      case 'dealclosingchange': return 'person_outline';
      case 'dealdescriptionchange': return 'person_outline';
      case 'dealstagedescriptionupdate': return 'person_outline';
      case 'dealpayment': return 'payments';
      default: return 'history_toggle_off'; // A generic fallback icon
    }
  }

  isEmailActivity(type: string): boolean {
    if (!type) return false;
    return type.toLowerCase().includes('email');
  }

  private relativeTimeCache = new Map<string, { time: number; result: string }>();

  // Returns relative time from the provided date string
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const now = Date.now();
    const cached = this.relativeTimeCache.get(dateString);
    if (cached && (now - cached.time < 5000)) {
      return cached.result;
    }

    const activityDate = new Date(dateString);
    if (isNaN(activityDate.getTime())) return '';

    const diffInSeconds = Math.floor((now - activityDate.getTime()) / 1000);

    const intervals: { [key: string]: number } = {
      'year': 31536000,
      'month': 2592000,
      'day': 86400,
      'hour': 3600,
      'minute': 60
    };

    let result = 'Just now';
    for (const intervalName in intervals) {
      const interval = intervals[intervalName];
      const count = Math.floor(diffInSeconds / interval);
      if (count > 0) {
        result = `${count} ${intervalName}${count > 1 ? 's' : ''} ago`;
        break;
      }
    }

    this.relativeTimeCache.set(dateString, { time: now, result });
    return result;
  }

  @Output() scrollChange = new EventEmitter<void>();


  onScroll(event: Event) {
    const target = event.target as HTMLElement;

    const threshold = 100;
    const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

    if (atBottom && this.onScrollValue) {
      console.log('Reached bottom, emitting scrollChange');
      this.scrollChange.emit();
    }
  }

  toggleEmailDetails(activity: any) {
    activity.showEmailDetails = !activity.showEmailDetails;
  }

  openActivityActionModal(activity: any) {
    this.dialog.open(ActivityActionModalComponent, {
      width: '600px',
      data: activity
    });
  }

  transcriptData: boolean = false;

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  protected _onDestroy = new Subject<void>();

  audioBlobUrls: { [url: string]: string } = {};

  public serverPath = environment.commonImageApi + '/courseAudio/';

  // getAudioUrl(schedule: string): string {
  //   if (!schedule) return '';
  //   let url = schedule;
  //   if (url.includes('flowzo.ai')) {

  //     const cid = localStorage.getItem(Constant.enterPriseKey);
  //     if (cid && !url.includes('cid=')) {
  //       url += (url.includes('?') ? '&' : '?') + 'cid=' + cid;
  //     }

  //     if (this.audioBlobUrls[schedule]) {
  //       return this.audioBlobUrls[schedule];
  //     }

  //     this.loadAudioAsBlob(schedule, url);
  //   } else {
  //     url = this.serverPath + schedule;
  //     const cid = localStorage.getItem(Constant.enterPriseKey);
  //     if (cid && !url.includes('cid=')) {
  //       url += (url.includes('?') ? '&' : '?') + 'cid=' + cid;
  //     }
  //   }


  //   return url;
  // }

  getAudioUrl(schedule: string): string {
    if (!schedule) return '';
    let url = schedule;
    if (url.startsWith('http://')) {
      url = url.replace('http://', 'https://');
    }
    if (url.includes('flowzo.ai')) {
      const cid = localStorage.getItem(Constant.enterPriseKey);
      if (cid && !url.includes('cid=')) {
        url += (url.includes('?') ? '&' : '?') + 'cid=' + cid;
      }
    }
    else {
      url = this.serverPath + schedule;
      const cid = localStorage.getItem(Constant.enterPriseKey);
      if (cid && !url.includes('cid=')) {
        url += (url.includes('?') ? '&' : '?') + 'cid=' + cid;
      }
    }
    return url;
  }

  loadAudioAsBlob(originalUrl: string, resolvedUrl: string): void {
    if (this.audioBlobUrls[originalUrl]) return;
    this.audioBlobUrls[originalUrl] = 'LOADING';

    fetch(resolvedUrl)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        this.audioBlobUrls[originalUrl] = objectUrl;
        this.cdr.detectChanges();
      })
      .catch(err => {
        console.error('Failed to load audio as blob:', err);
        this.audioBlobUrls[originalUrl] = resolvedUrl;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    // Revoke created blob URLs to free memory
    try {
      Object.values(this.audioBlobUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    } catch (e) { }
  }
}
